/**
 * @file pickedItemWords.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description The words a line takes when a person picks an item for it —
 *              the ONE rule every line editor uses (Cam, 28 Oct 2026: "On
 *              create order, invoice, bill, PO etc. when item is changed via
 *              the item picker please replace the sales/ purchase description
 *              with the newly selected item's").
 *
 *   An item's two wordings (Cam, 2026-10-02):
 *     Sale     — its Sales Description, else its name. A customer order's Sale
 *                box, an invoice or credit note line.
 *     Purchase — its Purchase Description, else its name. A customer order's
 *                Purchase box, a PO line, a bill or supplier credit line. NEVER
 *                the sales wording: on blends that names the customer, and the
 *                mill must not see our customer.
 *
 *   Which a line takes, by document (the grid's `variant`):
 *     order    — both: product_name (sale) and purchase_description (purchase)
 *     invoice  — product_name (sale)
 *     purchase — product_name (purchase)
 *     bill     — product_name (purchase)
 *
 *   ONLY WHEN THE ITEM CHANGES. A different item, or the first item on a line,
 *   replaces the words — typed or not — with the new item's. The same item
 *   picked again leaves the line's words alone (they are the document's
 *   record — a box left empty takes the item's). Nothing else here writes
 *   words: loading a saved document, the SO ↔ PO sync, server rebuilds and
 *   catalogue lookups never call this. The person can type over the new words
 *   straight after, as before.
 *
 *   Additive names are not words: every grid keeps them in their own column
 *   and the document puts them after the words when saved
 *   (utils/lineItems withAdditiveNames), so the new item's words simply take
 *   the line's additives after them.
 *
 *   The server copy (utils/pickedItemWords.js) is generated by
 *   scripts/sync-shared-utils.js — edit this file, then run it. The server's
 *   PO-side item correction (utils/afterDeliveryEdit saleTextOnItemChange)
 *   gives the order line the same sale words.
 *
 *   Pure.
 */

const trimmed = (v) => (v == null ? '' : String(v).trim());
const up = (v) => trimmed(v).toUpperCase();

/**
 * An item's name, whichever shape it arrived in: /api/orders/inventory-items
 * names it product_name, the inventory table (and /api/inventory/search)
 * item_name.
 */
const nameOf = (item) => trimmed(item?.item_name ?? item?.product_name);

/**
 * An item's SALE wording: its Sales Description, else its name. On a full
 * inventory row the Sales Description is `description`; the order screen's
 * list names it `sales_description`.
 */
export const itemSalesTextOf = (item) => trimmed(item?.sales_description ?? item?.description) || nameOf(item);

/** An item's PURCHASE wording: its Purchase Description, else its name. Never the sales wording. */
export const itemPurchaseTextOf = (item) => trimmed(item?.purchase_description) || nameOf(item);

/** The documents whose line words are the sale wording (the rest are purchase). */
export const SALE_VARIANTS = ['order', 'invoice'];

/**
 * The words an item gives a line of this kind of document.
 * @param {'order'|'invoice'|'purchase'|'bill'} variant
 * @param {object} item — an inventory item (either shape)
 * @returns {{ product_name?: string, purchase_description?: string }}
 */
export function itemWordsFor(variant, item) {
  if (!item) return {};
  if (variant === 'order') {
    return { product_name: itemSalesTextOf(item), purchase_description: itemPurchaseTextOf(item) };
  }
  if (variant === 'invoice') return { product_name: itemSalesTextOf(item) };
  return { product_name: itemPurchaseTextOf(item) };
}

/**
 * Is `item` a different item from the one the line carries — or the line's
 * first? By id where both have one, else by code (a saved line whose code
 * did not resolve to an item still has its code).
 * @param {object} line — the grid line (inventory_item_id, product_code)
 * @param {object} item
 */
export function isItemChange(line, item) {
  if (!item) return false;
  const lineId = line?.inventory_item_id;
  const itemId = item.id ?? item.inventory_item_id;
  const hasLineId = lineId !== null && lineId !== undefined && lineId !== '';
  if (hasLineId && itemId !== null && itemId !== undefined) return String(lineId) !== String(itemId);
  const lineCode = up(line?.product_code);
  if (!lineCode) return true;
  return lineCode !== up(item.item_code ?? item.product_code);
}

/**
 * The words to put on a line when `item` is picked for it: the new item's
 * when it is a different item (or the line's first); nothing when the same
 * item is picked again — except a box left empty, which takes the item's.
 * @returns {{ product_name?: string, purchase_description?: string }} — merge into the line
 */
export function wordsOnItemPick(variant, line, item) {
  if (!item) return {};
  const words = itemWordsFor(variant, item);
  if (isItemChange(line, item)) return words;
  const out = {};
  for (const [key, value] of Object.entries(words)) {
    if (!trimmed(line?.[key]) && value) out[key] = value;
  }
  return out;
}
